import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Download } from 'lucide-react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { mapAttendanceToPointage } from '../utils/attendanceMapper';
import { buildEmployeeLookups, enrichRecordsWithEmployeeData } from '../utils/employeeLookup';
import { flattenDepartments, buildParams, formatTimeLabel } from '../components/attendance/pointagesHelpers';
import PointagesStatsCards from '../components/attendance/PointagesStatsCards';
import PointagesBrutsFiltersCard from '../components/attendance/PointagesBrutsFiltersCard';
import PointagesBrutsListCard from '../components/attendance/PointagesBrutsListCard';

const ROWS_PER_PAGE = 50;
const todayStr = format(new Date(), 'yyyy-MM-dd');
const EMPTY_ARRAY = [];

const Pointages = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [dateRange, setDateRange] = useState({ start: todayStr, end: todayStr });
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const isDateRangeInvalid = Boolean(dateRange.start && dateRange.end && dateRange.start > dateRange.end);

  useEffect(() => {
    setSelectedEmployees([]);
  }, [selectedDepartment]);

  const [deptsQ, empQ, holidaysQ] = useQueries({
    queries: [
      {
        queryKey: ['pointages-departments'],
        queryFn: () =>
          api.get('v1/organization-chart/').then((r) =>
            flattenDepartments(r.data?.organization_chart || [])
          ),
      },
      {
        queryKey: ['pointages-employees', selectedDepartment],
        queryFn: () => {
          const params = new URLSearchParams({ limit: 500 });
          if (selectedDepartment) params.set('department_id', selectedDepartment);
          return api.get('v1/employees/', { params }).then((r) => r.data?.employees || []);
        },
      },
      {
        queryKey: ['pointages-holidays', dateRange.start, dateRange.end],
        queryFn: async () => {
          if (!dateRange.start || !dateRange.end) return [];
          try {
            const startDate = new Date(dateRange.start);
            const endDate = new Date(dateRange.end);
            const years = [];
            for (let year = startDate.getFullYear(); year <= endDate.getFullYear(); year++) years.push(year);
            const responses = await Promise.all(
              years.map((year) => api.get('v1/holidays/public-holidays', { params: { year, limit: 500 } }))
            );
            return responses.flatMap((res) => res.data.holidays || []).filter((h) => {
              const d = new Date(h.date);
              return d >= startDate && d <= endDate;
            });
          } catch {
            return [];
          }
        },
        enabled: !!dateRange.start && !!dateRange.end,
      },
    ],
  });

  const logsQ = useQuery({
    queryKey: ['attendance-logs', currentPage, dateRange, selectedDepartment, filterType, searchTerm, selectedEmployees],
    queryFn: () => {
      const params = buildParams({
        currentPage,
        dateRange,
        selectedDepartment,
        rowsPerPage: ROWS_PER_PAGE,
        filterType,
        searchTerm,
        selectedEmployees,
      });
      return api.get('v1/attendances/logs', { params }).then((r) => r.data || {});
    },
    enabled: empQ.isSuccess && !isDateRangeInvalid,
  });

  const departments = deptsQ.data ?? EMPTY_ARRAY;
  const employeesOptions = empQ.data ?? EMPTY_ARRAY;
  const holidays = holidaysQ.data ?? EMPTY_ARRAY;
  const employeeLookups = useMemo(() => buildEmployeeLookups(employeesOptions), [employeesOptions]);
  const rawItems = logsQ.data?.items ?? EMPTY_ARRAY;
  const totalPages = Math.max(1, Math.ceil((isDateRangeInvalid ? 0 : (logsQ.data?.total ?? rawItems.length)) / ROWS_PER_PAGE));
  const loading = logsQ.isPending && empQ.isSuccess;
  const stats = logsQ.data?.stats ?? {};
  const totalPointages = isDateRangeInvalid ? 0 : (stats.total ?? logsQ.data?.total ?? rawItems.length);
  const totalEntries = isDateRangeInvalid ? 0 : (stats.entries ?? 0);
  const totalExits = isDateRangeInvalid ? 0 : (stats.exits ?? 0);
  const uniqueEmployeesCount = isDateRangeInvalid ? 0 : (stats.unique_employees ?? 0);

  const normalizeLogs = useCallback((items) => {
    const normalized = (items || [])
      .map((item) => {
        const mapped = mapAttendanceToPointage(item);
        if (!mapped) return null;
        return {
          ...mapped,
          first_name: item.first_name,
          last_name: item.last_name,
          registration_number: mapped.registration_number || item.registration_number || item.user_id,
          employee_badge: mapped.employee_badge || item.employee_badge || item.user_id,
          employee_id: mapped.employee_id || item.employee_id,
          user_id: item.user_id,
          department_name: mapped.department_name || item.department_name,
        };
      })
      .filter(Boolean);
    return enrichRecordsWithEmployeeData(normalized, employeeLookups);
  }, [employeeLookups]);

  const pointages = useMemo(() => isDateRangeInvalid ? [] : normalizeLogs(rawItems), [isDateRangeInvalid, rawItems, normalizeLogs]);

  const handleDateChange = (field, value) => {
    setDateRange((prev) => ({ ...prev, [field]: value || '' }));
    setCurrentPage(1);
  };

  const handleFilterTypeChange = (value) => {
    setFilterType(value);
    setCurrentPage(1);
  };

  const handleSearchTermChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleEmployeesFilterChange = (value) => {
    setSelectedEmployees(value);
    setCurrentPage(1);
  };

  const filteredPointages = pointages;


  const groupedPointages = useMemo(() => {
    const groups = {};
    filteredPointages.forEach((ptg) => {
      const displayName =
        ptg.employee_display_name ||
        (ptg.employee_name && !ptg.employee_name.startsWith('Badge') ? ptg.employee_name : null) ||
        (ptg.registration_number ? `${t('attendance.raw.matricule')} ${ptg.registration_number}` : null) ||
        (ptg.employee_badge ? `${t('attendance.raw.badge')} ${ptg.employee_badge}` : t('attendance.raw.employeeFallback'));
      const key = ptg.employee_id || ptg.registration_number || ptg.employee_badge || ptg.id;
      if (!groups[key]) {
        groups[key] = {
          employee: {
            id: ptg.employee_id,
            registrationNumber: ptg.registration_number,
            badge: ptg.employee_badge,
            displayName,
            departmentName: ptg.department_name,
          },
          dates: {},
        };
      }
      const dateKey = ptg.timestamp ? format(new Date(ptg.timestamp), 'yyyy-MM-dd') : todayStr;
      if (!groups[key].dates[dateKey]) groups[key].dates[dateKey] = [];
      groups[key].dates[dateKey].push(ptg);
    });
    Object.values(groups).forEach((group) => {
      Object.keys(group.dates).forEach((dateKey) => {
        group.dates[dateKey].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      });
    });
    return groups;
  }, [filteredPointages, t]);

  const groupedList = useMemo(() => Object.entries(groupedPointages).map(([key, group]) => ({ key, ...group })), [groupedPointages]);

  const handleApplyFilters = () => {
    if (isDateRangeInvalid) return;
    setCurrentPage(1);
    queryClient.invalidateQueries({ queryKey: ['attendance-logs'] });
  };

  const exportPointages = async () => {
    if (isDateRangeInvalid || !totalPointages) return;
    const allItems = [];
    for (let skip = 0; skip < totalPointages; skip += 1000) {
      const params = buildParams({
        currentPage: Math.floor(skip / 1000) + 1,
        dateRange,
        selectedDepartment,
        rowsPerPage: 1000,
        filterType,
        searchTerm,
        selectedEmployees,
      });
      const response = await api.get('v1/attendances/logs', { params });
      allItems.push(...(response.data?.items || []));
      if (!response.data?.items?.length) break;
    }
    const exportPointages = normalizeLogs(allItems);
    const exportGroups = {};
    exportPointages.forEach((ptg) => {
      const displayName =
        ptg.employee_display_name ||
        (ptg.employee_name && !ptg.employee_name.startsWith('Badge') ? ptg.employee_name : null) ||
        (ptg.registration_number ? `${t('attendance.raw.matricule')} ${ptg.registration_number}` : null) ||
        (ptg.employee_badge ? `${t('attendance.raw.badge')} ${ptg.employee_badge}` : t('attendance.raw.employeeFallback'));
      const key = ptg.employee_id || ptg.registration_number || ptg.employee_badge || ptg.id;
      if (!exportGroups[key]) {
        exportGroups[key] = { employee: { registrationNumber: ptg.registration_number, badge: ptg.employee_badge, displayName }, dates: {} };
      }
      const dateKey = ptg.timestamp ? format(new Date(ptg.timestamp), 'yyyy-MM-dd') : todayStr;
      if (!exportGroups[key].dates[dateKey]) exportGroups[key].dates[dateKey] = [];
      exportGroups[key].dates[dateKey].push(ptg);
    });
    const rows = [];
    Object.values(exportGroups).forEach((group) => {
      Object.entries(group.dates).forEach(([dateKey, entries]) => {
        entries.forEach((entry) => {
          rows.push([
            group.employee.registrationNumber || group.employee.badge || 'N/A',
            group.employee.displayName,
            dateKey,
            formatTimeLabel(entry.timestamp),
            entry.type === 'entree' ? t('attendance.raw.entry') : t('attendance.raw.exit'),
            entry.device_id || entry.terminal_name || 'N/A',
          ]);
        });
      });
    });
    const csvContent = 'data:text/csv;charset=utf-8,' +
      [t('attendance.raw.matricule'), t('attendance.csv.employee'), t('attendance.csv.date'), t('attendance.csv.time'), t('attendance.csv.type'), t('attendance.csv.device')].join(',') + '\n' +
      rows.map((r) => r.map((value) => `"${value ?? ''}"`).join(',')).join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `pointages_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('attendance.raw.title')}</h1>
          <p className="text-muted-foreground">{t('attendance.raw.subtitle')}</p>
        </div>
        <Button variant="outline" onClick={exportPointages} disabled={!totalPointages || isDateRangeInvalid}>
          <Download className="mr-2 h-4 w-4" />
          {t('common.export')}
        </Button>
      </div>

      <PointagesStatsCards
        totalPunches={totalPointages}
        totalEntries={totalEntries}
        totalExits={totalExits}
        uniqueEmployeesCount={uniqueEmployeesCount}
      />

      {isDateRangeInvalid && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">
          {t('pointages.filters.invalidRange')}
        </div>
      )}

      <PointagesBrutsFiltersCard
        dateRange={dateRange}
        handleDateChange={handleDateChange}
        selectedDepartment={selectedDepartment}
        setSelectedDepartment={setSelectedDepartment}
        setCurrentPage={setCurrentPage}
        departments={departments}
        filterType={filterType}
        setFilterType={handleFilterTypeChange}
        searchTerm={searchTerm}
        setSearchTerm={handleSearchTermChange}
        employeesOptions={employeesOptions}
        selectedEmployees={selectedEmployees}
        handleEmployeesFilterChange={handleEmployeesFilterChange}
        employeesLoading={empQ.isPending}
        employeesReady={empQ.isSuccess}
        handleApplyFilters={handleApplyFilters}
      />

      <PointagesBrutsListCard
        loading={loading}
        groupedList={groupedList}
        filteredPointages={filteredPointages}
        totalCount={totalPointages}
        holidays={holidays}
        totalPages={totalPages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};

export default Pointages;
